import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Edit, Redo } from "@dreamhub/designsystem/icons";
import { DreamerModalHeaderSlots } from "@/components/global/Chat/components/dreamerAttachment/DreamerAttachmentModalShell";
import { ReportActionsMenu } from "@/components/global/Chat/components/dreamerReports/ReportActionsMenu";
import { ReportNameInput } from "@/components/global/Chat/components/dreamerReports/ReportNameInput";
import { useReportRename } from "@/components/global/Chat/components/dreamerReports/useReportRename";
import { ReportRowAction } from "@/components/global/Chat/components/dreamerReports/useReportRowActions";
import {
  useReportRun,
  useReportRuns
} from "@/components/global/Chat/components/dreamerReports/ReportRunsContext";
import { ACTIONS_TRANSLATION_KEY } from "@/components/global/Chat/components/dreamerReports/consts";
import { useFeatureFlag } from "@/services/features/hook";
import { FeatureFlags } from "@/services/features/types";
import { reportTargetWithoutOrigin } from "@/entities/savedReports/types";

type SaveControlRenderer = (options: {
  align?: "start" | "end";
  inReportView?: boolean;
}) => React.ReactNode;

interface UseReportModalHeaderParams {
  /** The saved report behind the answer, if it has one. */
  savedReportId: string | null;
  /** The report's own name, which a rename changes. */
  savedName?: string | null;
  /** The chat answer this view shows. */
  messageId: string;
  isReplayable: boolean;
  /**
   * Whether this view follows the report's runs itself, as a report opened
   * from the reports list does. An answer card in the chat shows only its own
   * answer, so a run started from it opens in the chat drawer's report view.
   */
  regeneratesInPlace: boolean;
  renderSaveControl: SaveControlRenderer;
  onClose: () => void;
}

/**
 * The top-right of a report's expanded view: the card's Save control, less
 * Manage reports, and, once the report is saved, a menu to run it again or
 * rename it in place.
 */
export const useReportModalHeader = ({
  savedReportId,
  savedName,
  messageId,
  isReplayable,
  regeneratesInPlace,
  renderSaveControl,
  onClose
}: UseReportModalHeaderParams): {
  /** The saved name, which the view shows over the answer's generated title. */
  title?: string;
  headerSlots: DreamerModalHeaderSlots;
} => {
  const { t } = useTranslation();
  const { enabled: isMyReportsEnabled } = useFeatureFlag(
    FeatureFlags.CRM_10285_MY_REPORTS
  );
  // A legacy message still carries its schedule row's id; saved reports exist only under My Reports.
  const reportId = isMyReportsEnabled ? savedReportId : null;
  const [isRenaming, setIsRenaming] = useState(false);
  const finishRename = useCallback(() => setIsRenaming(false), []);
  const { startRun, focusReport } = useReportRuns();
  const run = useReportRun(reportId);

  const rename = useReportRename({
    report: { id: reportId ?? "", name: savedName ?? "" },
    isRenaming,
    onFinish: finishRename
  });

  const runAgain = useCallback(() => {
    if (!reportId) return;
    startRun(reportId, savedName || null);
    if (regeneratesInPlace) return;
    onClose();
    focusReport(
      reportTargetWithoutOrigin({
        id: reportId,
        originMessageId: messageId,
        isReplayable,
        name: savedName ?? undefined
      })
    );
  }, [
    reportId,
    savedName,
    messageId,
    isReplayable,
    regeneratesInPlace,
    startRun,
    focusReport,
    onClose
  ]);

  const isGenerating = run?.status === "generating";

  const actions: ReportRowAction[] = useMemo(
    () => [
      {
        key: "runAgain",
        group: "primary",
        label: t(`${ACTIONS_TRANSLATION_KEY}.runAgain`),
        icon: <Redo />,
        onSelect: runAgain,
        disabled: !isReplayable || isGenerating,
        tooltip: isReplayable
          ? undefined
          : t(`${ACTIONS_TRANSLATION_KEY}.notReplayable`)
      },
      {
        key: "rename",
        group: "manage",
        label: t(`${ACTIONS_TRANSLATION_KEY}.rename`),
        icon: <Edit />,
        onSelect: () => setIsRenaming(true)
      }
    ],
    [runAgain, isReplayable, isGenerating, t]
  );

  return {
    title: reportId && savedName ? savedName : undefined,
    headerSlots: {
      titleContent: isRenaming ? (
        <div className="w-[271px]">
          <ReportNameInput
            ref={rename.inputRef}
            value={rename.draftName}
            onValueChange={rename.setDraftName}
            onCommit={rename.commitRename}
            onCancel={rename.cancelRename}
          />
        </div>
      ) : undefined,
      leadingActions: renderSaveControl({ align: "end", inReportView: true }),
      trailingActions: reportId ? (
        <ReportActionsMenu
          actions={actions}
          revealOnRowHover={false}
          size="lg"
        />
      ) : undefined
    }
  };
};
